import { useMemo } from 'react';

import { useIsTutorialMode } from '../../../context/PatientDataContext';
import type { InsuranceInfo } from '../../../types/types';
import type {
  CoverageDisplayStatus,
  CreatePatientCoverageRequest,
  OrganizedCoverages,
  PatientCoverage,
} from '../types';

import { useGetPatientCoveragesQuery } from './api';

export const normalizeCoverageKind = (kind?: string | null): string =>
  kind?.trim().toLowerCase().replace('-', '_') || 'insurance';

export const isSelfPayCoverage = (coverage?: Partial<PatientCoverage> | null): boolean =>
  normalizeCoverageKind(coverage?.kind) === 'self_pay';

export const getCoverageDisplayLabel = (
  coverage?: Partial<PatientCoverage & { display_label?: string | null }> | null,
): string | null => {
  if (!coverage) return null;
  if (isSelfPayCoverage(coverage)) return 'Self-pay';
  return coverage.display_label ?? coverage.payer_name ?? null;
};

export const buildSelfPayCoverageRequest = (coverageOrder = 1): CreatePatientCoverageRequest => ({
  coverage_order: coverageOrder,
  kind: 'self_pay',
  status: 'active',
  payer_id: null,
  payer_name: null,
  member_id: null,
  group_number: null,
  plan_name: null,
  relationship_to_subscriber: 'self',
  consent_on_file: false,
});

export const buildPendingInsuranceCoverageRequest = (
  coverageOrder = 1,
): CreatePatientCoverageRequest => ({
  coverage_order: coverageOrder,
  kind: 'insurance',
  status: 'pending',
  payer_id: null,
  payer_name: null,
  member_id: null,
  group_number: null,
  plan_name: null,
  relationship_to_subscriber: 'self',
  consent_on_file: false,
});

/**
 * Helper to convert empty/falsy values to null (for API)
 */
const toNullable = <T>(value: T | undefined | null | ''): T | null =>
  value === '' || value === undefined ? null : (value);

/**
 * Maps insurance data (legacy InsuranceInfo or PatientCoverage) to a coverage request format.
 * Use this when creating or updating coverages to ensure consistent field mapping.
 *
 * @param coverageOrder - 1 for primary, 2 for secondary, 3 for tertiary
 * @param insurance - The insurance data to map (can be legacy or new format)
 * @returns A properly formatted coverage request object
 */
export function mapInsuranceToCoverageRequest(
  coverageOrder: number,
  insurance: InsuranceInfo | PatientCoverage,
): CreatePatientCoverageRequest {
  // Helper to check if data is new format
  const isPatientCoverage = (data: InsuranceInfo | PatientCoverage): data is PatientCoverage =>
    'patient_insurance_coverage_id' in data;

  // Legacy format stores the subscriber name combined in insured_name;
  // split it so subscriber identity is never dropped on save (GDN-2317).
  // Split on whitespace runs — OCR'd names often carry double spaces.
  const legacyInsuredName =
    'insured_name' in insurance ? insurance.insured_name?.trim() || '' : '';
  const legacyNameParts = legacyInsuredName.split(/\s+/).filter(Boolean);
  const legacyFirstName = legacyNameParts[0] || null;
  const legacyLastName = legacyNameParts.slice(1).join(' ') || null;

  return {
    coverage_order: coverageOrder,
    kind: isPatientCoverage(insurance) ? normalizeCoverageKind(insurance.kind) : 'insurance',
    payer_id: toNullable(insurance.payer_id),
    payer_name: toNullable(insurance.payer_name),
    plan_name: toNullable('plan_name' in insurance ? insurance.plan_name : null),
    member_id: toNullable(
      'member_id' in insurance
        ? insurance.member_id
        : 'policy_number' in insurance
          ? insurance.policy_number
          : null,
    ),
    group_number: toNullable(insurance.group_number),
    claim_filing_code: toNullable(
      'claim_filing_code' in insurance ? insurance.claim_filing_code : null,
    ),
    relationship_to_subscriber:
      toNullable(
        'relationship_to_subscriber' in insurance
          ? insurance.relationship_to_subscriber
          : 'relationship_to_insured' in insurance
            ? insurance.relationship_to_insured
            : null,
      ) || 'self',
    consent_on_file:
      ('consent_on_file' in insurance
        ? insurance.consent_on_file
        : 'authorization_on_file' in insurance
          ? insurance.authorization_on_file
          : undefined) ?? undefined,
    subscriber_first_name: toNullable(
      'subscriber_first_name' in insurance ? insurance.subscriber_first_name : legacyFirstName,
    ),
    subscriber_last_name: toNullable(
      'subscriber_last_name' in insurance ? insurance.subscriber_last_name : legacyLastName,
    ),
    subscriber_dob: toNullable(
      'subscriber_dob' in insurance
        ? insurance.subscriber_dob
        : 'insured_dob' in insurance
          ? insurance.insured_dob
          : null,
    ),
    subscriber_sex: toNullable(
      'subscriber_sex' in insurance
        ? insurance.subscriber_sex
        : 'insured_sex' in insurance
          ? insurance.insured_sex
          : null,
    ),
    subscriber_address: toNullable(
      'subscriber_address' in insurance
        ? insurance.subscriber_address
        : 'insured_address' in insurance
          ? insurance.insured_address
          : null,
    ),
    // Coverage date range (GDN-2223) — patient_coverage-only fields, no
    // legacy InsuranceInfo equivalent. effective_date may also arrive from
    // the insurance-card OCR flow; either source flows through here.
    effective_date: toNullable('effective_date' in insurance ? insurance.effective_date : null),
    termination_date: toNullable(
      'termination_date' in insurance ? insurance.termination_date : null,
    ),
  };
}

/**
 * Backend-derived presentation status, with a client-side fallback for
 * cached responses that predate the field (GDN-2411).
 */
export function getCoverageDisplayStatus(
  coverage: Partial<PatientCoverage>,
): CoverageDisplayStatus {
  if (coverage.display_status) {
    return coverage.display_status as CoverageDisplayStatus;
  }
  if (coverage.status === 'cancelled') return 'cancelled';
  if (coverage.status === 'pending') return 'pending';
  const today = new Date().toISOString().slice(0, 10);
  if (coverage.effective_date && coverage.effective_date > today) return 'future';
  if (coverage.termination_date && coverage.termination_date < today) return 'expired';
  if (coverage.status === 'expired' && !coverage.termination_date) return 'expired';
  return 'active';
}

/** Display statuses that occupy an editor slot (history rows do not). */
const SLOT_DISPLAY_STATUSES = new Set<CoverageDisplayStatus>(['active', 'pending', 'future']);

const DISPLAY_STATUS_SLOT_RANK: Record<CoverageDisplayStatus, number> = {
  active: 0,
  pending: 1,
  future: 2,
  expired: 3,
  cancelled: 4,
};

function pickSlot(
  coverages: PatientCoverage[],
  order: number,
): { current?: PatientCoverage; incoming?: PatientCoverage } {
  const inSlot = coverages
    .filter(
      c =>
        c.coverage_order === order && SLOT_DISPLAY_STATUSES.has(getCoverageDisplayStatus(c)),
    )
    .sort(
      (a, b) =>
        DISPLAY_STATUS_SLOT_RANK[getCoverageDisplayStatus(a)] -
        DISPLAY_STATUS_SLOT_RANK[getCoverageDisplayStatus(b)],
    );
  const current = inSlot[0];
  // Any additional selectable row in the slot is the incoming/successor
  // plan — a mid-entry one is 'pending' until its start date is set, and
  // it must stay visible in the editor rather than vanish on first save.
  const incoming = inSlot.find(c => c !== current);
  return { current, incoming };
}

/**
 * Hook to get patient coverages organized by order (primary, secondary, tertiary)
 *
 * Fetches with include_inactive so expired/cancelled plans are available as
 * `history` (GDN-2411); slot organization only considers active/pending rows.
 */
export function useOrganizedPatientCoverages(patientId: string) {
  const isTutorialMode = useIsTutorialMode();
  const { data, isLoading, error, refetch } = useGetPatientCoveragesQuery(
    { patientId, includeInactive: true },
    { skip: isTutorialMode },
  );

  const organized = useMemo<OrganizedCoverages>(() => {
    if (!data?.coverages) {
      return {};
    }

    const primarySlot = pickSlot(data.coverages, 1);
    return {
      primary: primarySlot.current,
      incomingPrimary: primarySlot.incoming,
      secondary: pickSlot(data.coverages, 2).current,
      tertiary: pickSlot(data.coverages, 3).current,
    };
  }, [data]);

  const history = useMemo<PatientCoverage[]>(() => {
    if (!data?.coverages) return [];
    return data.coverages
      .filter(c => {
        const displayStatus = getCoverageDisplayStatus(c);
        return displayStatus === 'expired' || displayStatus === 'cancelled';
      })
      .sort((a, b) => (b.termination_date ?? '').localeCompare(a.termination_date ?? ''));
  }, [data]);

  return {
    coverages: data?.coverages || [],
    organized,
    history,
    coverageInForceToday: data?.coverage_in_force_today ?? null,
    nextCoverageGap: data?.next_coverage_gap ?? null,
    isLoading,
    error,
    refetch,
    total: data?.total || 0,
  };
}

/**
 * Hook to check if a patient has any active coverages
 */
export function useHasActiveCoverages(patientId: string) {
  const isTutorialMode = useIsTutorialMode();
  const { data, isLoading } = useGetPatientCoveragesQuery({ patientId }, { skip: isTutorialMode });

  const hasActive = useMemo(() => {
    if (!data?.coverages) return false;
    return data.coverages.some(c => getCoverageDisplayStatus(c) === 'active');
  }, [data]);

  return {
    hasActive,
    isLoading,
    count:
      data?.coverages?.filter(c => getCoverageDisplayStatus(c) === 'active').length || 0,
  };
}

/**
 * Helper to determine if a coverage needs subscriber information
 */
export function needsSubscriberInfo(coverage: Partial<PatientCoverage>): boolean {
  return coverage.relationship_to_subscriber !== 'self' && !!coverage.relationship_to_subscriber;
}

/**
 * Helper to get a display name for coverage order
 */
export function getCoverageOrderLabel(order: number): string {
  switch (order) {
    case 1:
      return 'Primary';
    case 2:
      return 'Secondary';
    case 3:
      return 'Tertiary';
    default:
      return `Coverage ${order}`;
  }
}
